import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { isEmpty, isFunction, noop } from 'mojito/utils';
import CombinationOddsPopup from 'modules/betslip/betslip-view/combination-odds-popup/index.js';
import BetslipModuleUtils from 'modules/betslip/utils.js';
import Odds from 'modules/betslip/betslip-view/bet-components/odds/index.js';
import BetErrors from 'modules/betslip/betslip-view/bet-components/bet-errors/index.js';
import withSportBetStake from 'modules/betslip/betslip-view/stake-input-hoc/index.js';
import StakeInputView from 'modules/stake-input/index.js';
import OddsDropdown from 'modules/betslip/betslip-view/bet-components/odds-dropdown/index.js';
import RemoveSelection from 'modules/betslip/betslip-view/bet-components/remove-selection/index.js';
import BonusIndicator from 'modules/bonus-indicator/index.js';
import BonusIndicatorTypes from 'modules/bonus-indicator/types.js';
import { useDispatch, useSelector } from 'react-redux';
import { useEventItem } from 'modules/common/hooks';

const StakeInput = withSportBetStake(StakeInputView);
const { LEG_SORT } = MojitoServices.Bets.types;

const { FlexPane, DateTime, Text, Button, Dropdown, SelectableButton, Spinner } =
    MojitoPresentation.Components;
const { usePreviousValue } = MojitoPresentation.Hooks;
const { EventFormatter, MarketHelper } = MojitoPresentation.Utils;
const BetsUtils = MojitoServices.Bets.utils;

const {
    actions: betslipActions,
    utils: BetslipUtils,
    types: BetslipTypes,
    helper: BetslipHelper,
} = MojitoServices.Betslip;

const { UPDATING, OPEN } = BetslipTypes.BETSLIP_STATUS;

const DefaultBetContent = props => {
    const dispatch = useDispatch();
    const {
        mojitoTools: { stringResolver, l10n, config },
        suspended,
        expired,
        showBanker,
        bet,
        isBankerPending,
        betslipStatus,
        isBetEachWay,
        betStake,
        hasHcapChange,
        hasOddsChange,
        showEachWay,
        showStake,
        onStakeInputFocus,
        onSelectionInfoClick,
        cbRemoveBet,
        isTeaser,
    } = props;

    const { stake = 0, freebetStake = 0, errors = [] } = betStake;

    const onClickBankers = useCallback(() => {
        dispatch(betslipActions.toggleBanker(bet.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bet.id]);

    const prices = useSelector(state => {
        const selections = BetslipHelper.getSelections(bet, state);
        return selections[0]?.prices;
    });

    const showOddsDropdown =
        prices &&
        !isTeaser &&
        prices.length > 1 &&
        prices.every(p => !p.disabled) &&
        LEG_SORT.TRICAST !== bet.legSort &&
        LEG_SORT.FORECAST !== bet.legSort &&
        LEG_SORT.REVERSE_FORECAST !== bet.legSort &&
        LEG_SORT.COMBINATION_TRICAST !== bet.legSort &&
        LEG_SORT.COMBINATION_FORECAST !== bet.legSort &&
        !bet.isBestPriceGuarantee;

    return (
        <>
            <BetErrors
                config={config.betErrors}
                errors={BetslipModuleUtils.getBetErrors(betStake, hasHcapChange)}
            />
            <FlexPane config={config.betslipBet.betInfoContainer} class="ta-BetInfoContainer">
                <>
                    <RemoveSelection
                        config={config.removeSelection}
                        bet={bet}
                        cbRemoveBet={cbRemoveBet}
                    />
                    <SelectionInfo
                        config={config}
                        bet={bet}
                        l10n={l10n}
                        stringResolver={stringResolver}
                        isBetEachWay={isBetEachWay}
                        suspended={suspended}
                        betslipStatus={betslipStatus}
                        onSelectionInfoClick={onSelectionInfoClick}
                    />
                    <FlexPane
                        config={config.betslipBet.stakeInfoContainer}
                        class="ta-StakeInfoContainer"
                    >
                        {showOddsDropdown ? (
                            <OddsDropdown
                                bet={bet}
                                prices={prices}
                                suspended={suspended}
                                expired={expired}
                                config={config.oddsDropdown}
                                hasOddsChange={hasOddsChange}
                            />
                        ) : (
                            <Odds
                                bet={bet}
                                config={config.odds}
                                suspended={suspended}
                                expired={expired}
                                hasOddsChange={hasOddsChange}
                            />
                        )}

                        {!suspended && !expired && showStake && (
                            <StakeInput
                                config={config.stakeInput}
                                betId={bet.id}
                                stakeForLine={stake || freebetStake}
                                isEachWayAvailable={showEachWay}
                                isEachWayActivated={isBetEachWay}
                                showStake={true}
                                isValid={errors.length === 0}
                                onFocusCallback={onStakeInputFocus}
                            />
                        )}
                        {showBanker && (
                            <BankerButton
                                isBanker={bet.isBanker}
                                isPending={isBankerPending}
                                label={stringResolver.resolveString('$BETSLIP.BANKER_BUTTON')}
                                onClick={onClickBankers}
                                betId={bet.id}
                                config={config}
                            />
                        )}
                    </FlexPane>
                </>
            </FlexPane>
        </>
    );
};

const SelectionInfo = props => {
    const {
        config,
        bet,
        l10n,
        stringResolver,
        isBetEachWay,
        suspended,
        expired,
        betslipStatus,
        onSelectionInfoClick,
    } = props;
    const isDisabled = !isFunction(onSelectionInfoClick) || onSelectionInfoClick === noop;
    return (
        <Button
            disabled={isDisabled}
            config={config.selectionInfoButton}
            onClick={onSelectionInfoClick}
            class="ta-SelectionInfoButton"
        >
            {BetsUtils.isMulticast(bet) ? (
                <MulticastSelectionInfo bet={bet} stringResolver={stringResolver} config={config} />
            ) : (
                <NormalSelectionInfo
                    config={config}
                    l10n={l10n}
                    bet={bet}
                    isBetEachWay={isBetEachWay}
                    suspended={suspended || expired}
                    stringResolver={stringResolver}
                    betslipStatus={betslipStatus}
                />
            )}
        </Button>
    );
};

const CombinationMulticastOdds = props => {
    const { parts, oddsLines, castMarketName, stringResolver, config } = props;
    const [isCombinationOddsDialogVisible, setCombinationOddsDialogVisible] = useState(false);

    if (!parts || !oddsLines || !castMarketName) {
        return null;
    }

    const onClickShowOdds = e => {
        e.stopPropagation();
        setCombinationOddsDialogVisible(true);
    };

    const onCloseCombinationOddsDialog = e => {
        e.stopPropagation();
        setCombinationOddsDialogVisible(false);
    };

    return (
        <>
            <Button
                class="ta-ShowOdds"
                config={config.multicastAllOddsButton}
                onClick={onClickShowOdds}
            >
                {stringResolver.resolveString('$BETSLIP.COMBINATION_SHOW_ODDS')}
            </Button>
            <CombinationOddsPopup
                isVisible={isCombinationOddsDialogVisible}
                parts={parts}
                oddsLines={oddsLines}
                headerTitle={castMarketName}
                onClickOutside={onCloseCombinationOddsDialog}
                onCloseButtonClick={onCloseCombinationOddsDialog}
            />
        </>
    );
};

const formatEventName = (startTime, name) => EventFormatter.formatEventName(name, startTime);

const EventName = props => {
    const { name, config, startTime } = props;

    return startTime ? (
        <DateTime
            config={config.eventNameTimeFormat}
            dateTime={startTime}
            dateTimeRenderer={formatEventName}
            rendererData={name}
        />
    ) : (
        formatEventName('', name)
    );
};

const NormalSelectionInfo = props => {
    const { config, bet, isBetEachWay, suspended, stringResolver, betslipStatus, l10n } = props;
    const [manuallySelectedMarketType, setManuallySelectedMarketType] = useState(undefined);
    const prevBetslipStatus = usePreviousValue(betslipStatus);
    const dispatch = useDispatch();
    const earlyPayoutPart = BetsUtils.getEarlyPayoutPart(bet);
    const hasEarlyPayout = Boolean(earlyPayoutPart);
    const bonusInfo = useMemo(
        () => ({ rules: earlyPayoutPart?.earlyPayout?.rules }),
        [earlyPayoutPart?.earlyPayout]
    );

    useEffect(() => {
        if (
            prevBetslipStatus === UPDATING &&
            betslipStatus === OPEN &&
            !!manuallySelectedMarketType
        ) {
            setManuallySelectedMarketType(undefined);
        }
    }, [betslipStatus, manuallySelectedMarketType, prevBetslipStatus]);

    let marketNameDisplay = '';
    let nameAndHandicapLabel = '';

    const part = BetslipUtils.getFirstBetPart(bet); // Regular bet will only have one part
    const { marketName, eachWayTerms, startTime, eventName, marketType, eventId } = part.partInfo;
    const { alternativeSelections, selectionId } = part;
    const event = useEventItem(eventId);
    const { marketSwitchItemsOverride } = config;

    const marketItems = !isEmpty(alternativeSelections)
        ? BetslipModuleUtils.resolveAlternativeMarkets(
              part,
              event?.participants,
              marketSwitchItemsOverride,
              (key, startingPitcher) => stringResolver.resolveAndFormatString(key, startingPitcher)
          ).map(item => ({ value: item.type, label: item.name }))
        : [];

    if (marketName) {
        marketNameDisplay = isBetEachWay
            ? MarketHelper.getEachWayLabel(eachWayTerms, l10n)
            : marketName;
        nameAndHandicapLabel = part
            ? BetslipModuleUtils.resolveNameAndHandicapLabel(part, l10n)
            : '';
    }

    const onMarketSwitch = value => {
        const newMarketType = alternativeSelections.find(({ marketType }) => marketType === value);
        setManuallySelectedMarketType(value);
        dispatch(betslipActions.switchSelection({ selectionId, betRef: newMarketType.betRef }));
    };

    return (
        <FlexPane config={config.selectionInfoWrapper} class="ta-SelectionInfoWrapper">
            <Text
                config={suspended ? config.selectionNameSuspended : config.selectionName}
                class="ta-selectionName"
            >
                {nameAndHandicapLabel}
            </Text>
            <Text
                config={suspended ? config.marketNameSuspended : config.marketName}
                class="ta-marketName"
            >
                {marketNameDisplay}
            </Text>
            {!isEmpty(alternativeSelections) && (
                <Dropdown
                    class="ta-MarketSwitchDropdown"
                    config={config.marketSwitchDropdown}
                    selectedItemValue={manuallySelectedMarketType || marketType}
                    items={marketItems}
                    cbOnChange={onMarketSwitch}
                    triggerOverlayInteraction={false}
                    disabled={BetslipUtils.isUpdating(betslipStatus)}
                />
            )}
            <Text
                config={suspended ? config.eventNameSuspended : config.eventName}
                class="ta-eventName"
            >
                {eventName && <EventName startTime={startTime} name={eventName} config={config} />}
            </Text>
            {hasEarlyPayout && (
                <BonusIndicator
                    class="ta-earlyPayoutIndicator"
                    config={config.earlyPayoutIndicator}
                    bonusType={BonusIndicatorTypes.TYPES.EARLY_PAYOUT}
                    bonusInfo={bonusInfo}
                    isAwarded={earlyPayoutPart.earlyPayout.awarded}
                />
            )}
        </FlexPane>
    );
};

const MulticastSelectionInfo = props => {
    const { bet, stringResolver, config } = props;
    const { oddsLines = [], parts, numberOfLines = 1, legSort } = bet;

    const multicastSelectionName =
        parts && parts.map(part => `${part.selectionName} (${part.number})`).join(', ');

    let castMarketName = stringResolver.resolveString(`$BETSLIP.MARKET_TYPES.${legSort}`);
    if (numberOfLines > 1) {
        castMarketName = stringResolver.resolveAndFormatString(
            '$BETSLIP.COMBINATION_CAST_WITH_LINES',
            castMarketName,
            numberOfLines
        );
    }

    const showCombinationMulticastOdds = oddsLines.length > 1;

    // Get event name and start time of any bet part.
    const { eventName, startTime } = BetslipUtils.getFirstBetPart(bet).partInfo;

    return (
        <FlexPane
            config={config.multicastSelectionInfoContainer}
            class="ta-MulticastSelectionInfoContainer"
        >
            <Text config={config.multicastSelectionName} class="ta-MulticastSelectionName">
                {multicastSelectionName}
            </Text>
            <Text config={config.multicastMarketName} class="ta-MulticastMarketName">
                {castMarketName}
            </Text>
            {showCombinationMulticastOdds && (
                <CombinationMulticastOdds
                    parts={parts}
                    oddsLines={oddsLines}
                    castMarketName={castMarketName}
                    stringResolver={stringResolver}
                    config={config}
                />
            )}
            <Text config={config.multicastEventName} class="ta-MulticastEventName">
                {eventName && <EventName startTime={startTime} name={eventName} config={config} />}
            </Text>
        </FlexPane>
    );
};

const BankerButton = props => {
    const { isBanker, isPending, label, onClick, config } = props;
    const [showSpinner, setShowSpinner] = useState(false);
    const timeoutRef = useRef();
    const onClickFixed = () => !isPending && onClick();
    const spinnerConfig = isBanker ? config.bankerSpinnerSelected : config.bankerSpinner;
    // Optimistically switch to the future state if pending
    const selected = isPending ? !isBanker : isBanker;

    useEffect(() => {
        if (isPending) {
            timeoutRef.current = setTimeout(
                () => setShowSpinner(true),
                config.showBankerSpinnerDelay
            );
        } else {
            setShowSpinner(false);
        }
        return () => clearTimeout(timeoutRef.current);
    }, [isPending, config.showBankerSpinnerDelay]);

    return (
        <SelectableButton
            selected={selected}
            onClick={onClickFixed}
            config={config.bankerButton}
            class="ta-BankerButton"
        >
            {showSpinner ? (
                <Spinner config={spinnerConfig} class="ta-BankerButtonSpinner" />
            ) : (
                label
            )}
        </SelectableButton>
    );
};

export default DefaultBetContent;
